import type { ComponentType } from 'react';
import { warnOnce } from '@/lib/warnOnce';
import { BookingsWidget } from '@/widgets/BookingsWidget/BookingsWidget';
import { DateWidget } from '@/widgets/DateWidget/DateWidget';
import { DepartureWidget } from '@/widgets/DepartureWidget/DepartureWidget';
import { DoughnutWidget } from '@/widgets/DoughnutWidget/DoughnutWidget';
import { FloorOccupancyWidget } from '@/widgets/FloorOccupancyWidget/FloorOccupancyWidget';
import { GroupWidget } from '@/widgets/GroupWidget';
import { HourlyChartWidget } from '@/widgets/HourlyChartWidget/HourlyChartWidget';
import { ImageLabelWidget } from '@/widgets/ImageLabelWidget/ImageLabelWidget';
import { widgetTypeOwners } from '@/widgets/knownWidgetTypes';
import { Logo } from '@/widgets/Logo/Logo';
import { ProgressBarWidget } from '@/widgets/ProgressBar/ProgressBar';
import { TextWidget } from '@/widgets/TextWidget/TextWidget';
import { TopSpacesWidget } from '@/widgets/TopSpacesWidget/TopSpacesWidget';
import { TravelWidget } from '@/widgets/TravelWidget/TravelWidget';
import { WeatherWidget } from '@/widgets/WeatherWidget/WeatherWidget';

/**
 * `component.type` -> React component, and the props contract every widget implements.
 *
 * Replaces the `components: { … }` block of legacy `moduleLoader.vue`, which registered
 * fourteen widgets with Vue's global component resolution. (`utils/functions.ts` also had a
 * `getWidgets()` returning a shorter list; it was dead code by the end and is not ported.)
 *
 * ---
 *
 * ## The widget contract
 *
 * ```ts
 * export type WidgetProps = {
 *   componentId: string;              // stable for the lifetime of the page
 *   options: Record<string, unknown>; // the config node's `options`, unvalidated
 * };
 * ```
 *
 * Two props. Nothing else. The legacy widgets received `v-bind="component"` — the whole
 * config node spread — plus explicit `options` and `idComp`, so `type`, `slide` and
 * `options` all arrived redundantly and a widget could reach for anything on the node. They
 * cannot here, and that is the point: a widget's entire input surface is its own `options`.
 *
 * **Each widget parses its own `options` with its own Zod schema.** `componentSchema` in
 * `src/config/schema.ts` deliberately leaves `options` an opaque `Record<string, unknown>`.
 * Two reasons, both structural:
 *
 * 1. Modelling fifteen widgets' options centrally would make `schema.ts` a merge bottleneck
 *    across SYM-495, SYM-496 and SYM-501 — three tickets editing one file.
 * 2. A central schema fails the *whole config* on one malformed widget, which blanks the
 *    screen. A per-widget schema fails one widget, which is CLAUDE.md constraint 4.
 *
 * On a schema failure a widget warns and renders nothing (or its empty state). It never
 * throws — though `WidgetBoundary` catches it if it does.
 *
 * ### A minimal widget
 *
 * ```tsx
 * import { z } from 'zod';
 * import { useWidgetData } from '@/hooks/useWidgetData';
 * import { useWidgetLayout } from '@/hooks/useWidgetLayout';
 * import { widgetLayoutSchema } from '@/config/schema';
 * import type { WidgetProps } from '@/widgets/registry';
 *
 * const optionsSchema = z.object({
 *   topic: z.string().optional(),
 *   layout: widgetLayoutSchema.optional(),
 *   label: z.string().default(''),
 * });
 *
 * export function ExampleWidget({ componentId, options }: WidgetProps) {
 *   const parsed = optionsSchema.safeParse(options);
 *   // Hooks run before any early return — never call one conditionally.
 *   const opts = parsed.success ? parsed.data : undefined;
 *   const data = useWidgetData<{ value: number }>(componentId, opts?.topic);
 *   const { className, style } = useWidgetLayout(opts?.layout);
 *
 *   if (!parsed.success) {
 *     console.warn('[signage] ExampleWidget options rejected', z.prettifyError(parsed.error));
 *     return null;
 *   }
 *   return <div className={className} style={style}>{opts.label}: {data?.value ?? '—'}</div>;
 * }
 * ```
 *
 * ### Where the transform comes from — decided once, here
 *
 * A widget **never** looks up its own transform. `WidgetSlot` registers the widget's topic
 * together with `transformsByWidgetType[component.type]` on its behalf, because the slot is
 * the only place that knows the config `type` — the widget itself does not receive it.
 *
 * A widget still calls `useWidgetData(componentId, options.topic)` to *read* its data, and
 * that second registration is a harmless no-op: `registerTopic` is first-wins on the topic
 * (same component id, so no duplicate warning) and leaves the already-stored transform alone
 * when none is passed. Do not pass a transform from a widget; there is exactly one place
 * transforms are wired and this is it.
 *
 * ---
 *
 * ## Adding a widget
 *
 * 1. Write it under `src/widgets/<name>/`, taking `WidgetProps`.
 * 2. Add it to `widgetRegistry` below. That is the whole change — `pendingWidgetTypes` is
 *    derived, so its placeholder disappears on its own.
 * 3. A brand-new type (one the legacy app never had) also needs a row in
 *    `knownWidgetTypes.ts`, or `pnpm check:configs` will reject the config that uses it.
 */

export type WidgetProps = {
  /** Stable for the lifetime of the page; the store key for this widget's data. */
  componentId: string;
  /** The config node's `options` object, unvalidated. The widget owns its schema. */
  options: Record<string, unknown>;
};

export type WidgetComponent = ComponentType<WidgetProps>;

/**
 * Implemented widgets, keyed by the `type` string in the config.
 *
 * Keys are config values and therefore as immovable as any other config key (CLAUDE.md
 * constraint 1) — a rename here silently blanks a widget on every display using it.
 *
 * Note the import cycle this creates: `GroupWidget` renders `WidgetSlot`, which calls
 * `resolveWidget` from this module. That is inherent to a recursive renderer and it is safe,
 * because the cycle is only closed at *render* time — nothing below reads the registry while
 * the modules are still evaluating.
 */
export const widgetRegistry: Record<string, WidgetComponent> = {
  GroupWidget,
  Logo,
  TextWidget,
  DateWidget,
  ImageLabelWidget,
  // The registry entry is the adapter; SYM-492's slider renders the prop-driven `ProgressBar`
  // from the same module instead, because the slider owns `interval` and `layout`.
  ProgressBar: ProgressBarWidget,
  DoughnutWidget,
  HourlyChartWidget,
  FloorOccupancyWidget,
  TopSpacesWidget,
  WeatherWidget,
  DepartureWidget,
  TravelWidget,
  BookingsWidget,
};

/**
 * Types the migration knows about but has not ported yet, mapped to the ticket that owns
 * them. These render `PlaceholderWidget` instead of nothing, so the layout is real and
 * reviewable before a single widget exists.
 *
 * Derived rather than hand-maintained: it is `knownWidgetTypes.ts` minus whatever
 * `widgetRegistry` already covers. Implementing a widget is therefore one edit, and the two
 * lists cannot drift into claiming a type is both done and pending.
 */
export const pendingWidgetTypes: Record<string, string> = Object.fromEntries(
  Object.entries(widgetTypeOwners).filter(([type]) => !(type in widgetRegistry)),
);

/** What `WidgetSlot` should render for a given config `type`. */
export type WidgetResolution =
  /** Implemented: render it. */
  | { kind: 'widget'; Widget: WidgetComponent }
  /** Known to the migration, not ported yet: render the labelled placeholder. */
  | { kind: 'placeholder'; ticket: string }
  /** Not a widget this app has ever had: render nothing, exactly as legacy Vue did. */
  | { kind: 'unknown' };

/**
 * Resolves a config `type` to what should be rendered for it.
 *
 * The `unknown` branch is legacy parity: Vue's dynamic `<component :is>` logged an
 * "unknown component" warning and rendered an empty node, and a typo in a config must cost
 * one widget, never the page. The distinction from `placeholder` is deliberate — a
 * placeholder box on a wall says "this is coming", an unrecognised type says "this config is
 * wrong", and the two should not look the same during review.
 *
 * Warnings are deduplicated per type (`warnOnce`): this runs on every render, and a display
 * renders for weeks.
 */
export function resolveWidget(type: string): WidgetResolution {
  const Widget = widgetRegistry[type];
  if (Widget) return { kind: 'widget', Widget };

  const ticket = pendingWidgetTypes[type];
  if (ticket !== undefined) {
    warnOnce(
      `widget-pending:${type}`,
      '[signage] widget type "%s" is not implemented yet (%s); rendering a placeholder',
      type,
      ticket,
    );
    return { kind: 'placeholder', ticket };
  }

  warnOnce(
    `widget-unknown:${type}`,
    '[signage] unknown widget type "%s" — check the `type` values in this signage config. ' +
      'Rendering nothing for it.',
    type,
  );
  return { kind: 'unknown' };
}
